import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { Label } from '@/shared/ui/Label';
import { PhoneInput } from '@/shared/ui/PhoneInput';

export interface FormPhoneFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues, unknown, unknown>;
  label: string;
  id: string;
  placeholder?: string;
  required?: boolean;
}

/**
 * React Hook Form field wired to {@link PhoneInput} (intl-tel-input).
 */
export function FormPhoneField<TFieldValues extends FieldValues>({
  name,
  control,
  label,
  id,
  placeholder = 'Enter phone number',
  required = false,
}: FormPhoneFieldProps<TFieldValues>): JSX.Element {
  return (
    <div>
      <Label htmlFor={id} className="mb-1">
        {label}
        {required ? <span className="ml-0.5 text-coral-500">*</span> : null}
      </Label>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <PhoneInput
              id={id}
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder={placeholder}
              required={required}
              error={!!fieldState.error}
            />
            {fieldState.error?.message ? (
              <p className="text-xs text-coral-600 mt-1 leading-tight" role="alert">
                {fieldState.error.message}
              </p>
            ) : null}
          </>
        )}
      />
    </div>
  );
}
